import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
} from "react";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
import { auth } from "../firebase";
import type { User } from "../types";
import { ADMIN_CONFIG } from "../constants";
import {
	getStorage,
	ref,
	uploadBytes,
	getDownloadURL,
	deleteObject,
} from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { doc, updateDoc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { PreviewService } from "../services/previewService";

interface AuthContextType {
	user: User | null;
	loading: boolean;
	isAdmin: boolean;
	updateProfilePhoto: (file: File | null) => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType>({
	user: null,
	loading: true,
	isAdmin: false,
	updateProfilePhoto: async () => {
		throw new Error("AuthContext not initialized");
	},
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [isAdmin, setIsAdmin] = useState(false);

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(
			auth,
			async (firebaseUser: FirebaseUser | null) => {
				if (firebaseUser) {
					setUser({
						uid: firebaseUser.uid,
						displayName: firebaseUser.displayName,
						email: firebaseUser.email,
						photoURL: firebaseUser.photoURL,
					});
					const email = firebaseUser.email || "";
					const uid = firebaseUser.uid || "";
					const adminFlag =
						(email && ADMIN_CONFIG.emails.includes(email)) ||
						(uid && ADMIN_CONFIG.uids.includes(uid));
					setIsAdmin(!!adminFlag);

					// Create user profile if it doesn't exist
					try {
						const profileRef = doc(db, "userProfiles", firebaseUser.uid);
						const profileSnap = await getDoc(profileRef);

						if (!profileSnap.exists()) {
							const defaultProfile = {
								uid: firebaseUser.uid,
								displayName: firebaseUser.displayName,
								email: firebaseUser.email,
								photoURL: firebaseUser.photoURL,
								badges: [],
								points: 0,
								joinDate: new Date(),
								contributionStats: {
									guidesCreated: 0,
									forumPosts: 0,
									forumReplies: 0,
									helpfulAnswers: 0,
								},
								preferences: {
									notifications: {
										replies: true,
										mentions: true,
										badges: true,
										weeklyDigest: true,
									},
									privacy: {
										showEmail: false,
										showActivity: true,
									},
								},
								isAdmin: adminFlag, // Set admin flag in database
							};
							await setDoc(profileRef, defaultProfile);

							// Clean up old preview guides for new user
							try {
								await PreviewService.cleanupUserPreviews(firebaseUser.uid);
							} catch (error) {
								console.error("Failed to cleanup user previews:", error);
							}
						} else {
							// Update existing profile if isAdmin status changed
							const existingData = profileSnap.data();
							if (existingData?.isAdmin !== adminFlag) {
								await updateDoc(profileRef, {
									isAdmin: adminFlag,
								});
							}

							// Clean up old preview guides for this user
							try {
								await PreviewService.cleanupUserPreviews(firebaseUser.uid);
							} catch (error) {
								console.error("Failed to cleanup user previews:", error);
							}
						}
					} catch (error) {
						console.error("Error creating/updating user profile:", error);
					}
				} else {
					setUser(null);
					setIsAdmin(false);
				}
				setLoading(false);
			}
		);

		return () => unsubscribe();
	}, []);

	const updateProfilePhoto = async (file: File | null) => {
		if (!user) throw new Error("No user logged in");

		try {
			// אם נשלח null, הסר את תמונת הפרופיל
			if (file === null) {
				// מחיקת תמונת פרופיל קודמת אם קיימת וזהו קובץ ב-Firebase Storage
				if (
					user.photoURL &&
					user.photoURL.startsWith("https://firebasestorage.googleapis.com/")
				) {
					const storage = getStorage();
					const oldPhotoRef = ref(storage, user.photoURL);
					try {
						await deleteObject(oldPhotoRef);
					} catch (deleteError) {
						console.warn("Could not delete old profile photo", deleteError);
					}
				}

				// עדכון פרופיל המשתמש ללא תמונה
				await updateProfile(auth.currentUser!, { photoURL: null });

				// עדכון מסמך המשתמש בFirestore
				const userDocRef = doc(db, "userProfiles", user.uid);
				await setDoc(
					userDocRef,
					{
						photoURL: null,
						displayName: user.displayName,
						email: user.email,
					},
					{ merge: true }
				);

				// עדכון state של המשתמש
				setUser((prev) => (prev ? { ...prev, photoURL: null } : null));

				return null;
			}

			// מחיקת תמונת פרופיל קודמת אם קיימת וזהו קובץ ב-Firebase Storage
			if (
				user.photoURL &&
				user.photoURL.startsWith("https://firebasestorage.googleapis.com/")
			) {
				const storage = getStorage();
				const oldPhotoRef = ref(storage, user.photoURL);
				try {
					await deleteObject(oldPhotoRef);
				} catch (deleteError) {
					console.warn("Could not delete old profile photo", deleteError);
				}
			}

			// יצירת הפניה לאחסון
			const storage = getStorage();
			const storageRef = ref(
				storage,
				`profilePhotos/${user.uid}/${Date.now()}_${file.name}`
			);

			// העלאת התמונה
			const snapshot = await uploadBytes(storageRef, file);

			// קבלת כתובת URL של התמונה
			const photoURL = await getDownloadURL(snapshot.ref);

			// וודא שה-URL תקין
			if (!photoURL || !photoURL.startsWith("https://")) {
				throw new Error("Invalid photo URL");
			}

			// עדכון פרופיל המשתמש
			await updateProfile(auth.currentUser!, { photoURL });

			// עדכון מסמך המשתמש בFirestore
			const userDocRef = doc(db, "userProfiles", user.uid);

			// צור את המסמך אם הוא לא קיים
			await setDoc(
				userDocRef,
				{
					photoURL,
					displayName: user.displayName,
					email: user.email,
				},
				{ merge: true }
			);

			// עדכון state של המשתמש
			setUser((prev) => (prev ? { ...prev, photoURL } : null));

			return photoURL;
		} catch (error) {
			console.error("Error updating profile photo:", error);

			// טיפול בסוגי שגיאות ספציפיים
			if (error instanceof Error) {
				if (error.message.includes("storage/invalid-url")) {
					throw new Error(
						"הקובץ שנבחר אינו תמונה תקינה. אנא בחר קובץ תמונה אחר."
					);
				}
				if (error.message.includes("storage/unauthorized")) {
					throw new Error("אין הרשאה להעלות תמונה. אנא בדוק את ההרשאות.");
				}
				if (error.message.includes("storage/quota-exceeded")) {
					throw new Error("חרגת ממגבלת האחסון. אנא פנה לתמיכה.");
				}
				if (error.message.includes("storage/canceled")) {
					throw new Error("העלאת התמונה בוטלה.");
				}
			}

			throw error;
		}
	};

	const contextValue = {
		user,
		loading,
		isAdmin,
		updateProfilePhoto, // הוספת הפונקציה לערך ההקשר
	};

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
};

export const useAuth = () => useContext(AuthContext);
